// src/components/jobDetails/WellInfoTab.jsx
import { useState } from 'react';

const WellMap = ({ northCoord, eastCoord, wellName }) => {
  const hasCoordinates = northCoord && eastCoord;
  
  return (
    <div className="bg-gradient-to-br from-slate-50 dark:from-gray-800 to-blue-50 dark:to-gray-900 rounded-xl p-4 h-48 relative overflow-hidden border border-gray-200">
      {hasCoordinates ? (
        <>
          <div className="absolute inset-0 opacity-10">
            <div className="grid grid-cols-6 grid-rows-6 h-full">
              {Array.from({ length: 36 }).map((_, i) => (
                <div key={i} className="border border-gray-200" />
              ))}
            </div>
          </div>
          
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              <div className="w-6 h-6 bg-red-500 rounded-full border-3 border-white shadow-md"></div>
              <div className="absolute -top-1 -left-1 w-8 h-8 bg-red-300 rounded-full opacity-20 animate-pulse"></div>
            </div>
          </div>

          <div className="absolute bottom-3 left-3 bg-white/80 backdrop-blur-sm rounded-lg p-2 shadow-xs">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Coordinates</div>
            <div className="font-mono text-xs font-medium">
              N: {northCoord} | E: {eastCoord}
            </div>
          </div>

          <div className="absolute top-3 left-3 bg-white/80 backdrop-blur-sm rounded-lg p-2 shadow-xs">
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Well</div>
            <div className="text-xs font-medium">{wellName}</div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-400">
            <div className="text-2xl mb-1">🗺️</div>
            <div className="text-xs">Coordinates not available</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function WellInfoTab({ wellInfo }) {
  const [copiedField, setCopiedField] = useState(null);

  if (!wellInfo) {
    return (
      <div className="bg-white rounded-xl p-6 text-center">
        <div className="text-gray-400 text-sm">No well information available</div>
      </div>
    );
  }

  const copyToClipboard = (text, fieldName) => {
    if (!text || text === "-") return;
    navigator.clipboard.writeText(text.toString());
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const DataCard = ({ title, value, unit, icon, category, fieldName }) => (
    <div className="bg-white rounded-lg p-3 border border-gray-150  dark:from-gray-800 dark:to-gray-900 hover:border-gray-300 transition-colors group">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 group-hover:text-blue-500 transition-colors">{icon}</span>
          <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">{title}</h3>
        </div>
        {value && value !== "-" && (
          <button
            onClick={() => copyToClipboard(value, fieldName)}
            className="text-gray-300 hover:text-gray-600 transition-colors text-xs"
            title="Copy to clipboard"
          >
            {copiedField === fieldName ? '✓' : '📋'}
          </button>
        )}
      </div>
      <div className="space-y-1">
        <div className="text-lg font-semibold text-gray-900 font-mono">
          {value || "-"}
        </div>
        {unit && (
          <div className="text-xs text-gray-400">{unit}</div>
        )}
        {category && (
          <div className="text-[10px] text-gray-300 uppercase tracking-wide mt-1">{category}</div>
        )}
      </div>
    </div>
  );

  const Section = ({ title, description, children, columns = 2 }) => (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">{title}</h3>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      <div className={`grid grid-cols-1 ${columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3`}>
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-gray-800">Well Information</h1>
          <p className="text-xs text-gray-500">Comprehensive well data and coordinates</p>
        </div>
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
          <span className="text-sm">⛰️</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column - Map & Quick Stats */}
        <div className="space-y-4">
          <WellMap 
            northCoord={wellInfo.north_coordinates} 
            eastCoord={wellInfo.easting_value} 
            wellName={wellInfo.well_name}
          />

          <div className="bg-white rounded-xl p-4 border border-gray-150">
            <Section title="Location Overview" description="Primary coordinate information">
              <DataCard
                title="Latitude"
                value={wellInfo.north_coordinates ? `${wellInfo.north_coordinates}°N` : undefined}
                icon="📍"
                fieldName="latitude"
                category="Decimal"
              />
              <DataCard
                title="Longitude"
                value={wellInfo.east_coorinates ? `${wellInfo.east_coorinates}°E` : undefined}
                icon="📍"
                fieldName="longitude"
                category="Decimal"
              />
              <DataCard
                title="Northing"
                value={wellInfo.north_coordinates}
                unit="UTM"
                icon="⬆️"
                fieldName="northing"
                category="Coordinate"
              />
              <DataCard
                title="Easting"
                value={wellInfo.easting_value}
                unit="UTM"
                icon="➡️"
                fieldName="easting"
                category="Coordinate"
              />
            </Section>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-150">
            <Section title="Reference System" description="Geodetic and elevation references">
              <DataCard
                title="Geodetic System"
                value={wellInfo.geodetic_system}
                icon="🌐"
                fieldName="geodetic"
                category="System"
              />
              <DataCard
                title="Geodetic Datum"
                value={wellInfo.geodetic_datum}
                icon="📋"
                fieldName="datum"
                category="Datum"
              />
              <DataCard
                title="Map Zone"
                value={wellInfo.map_zone}
                icon="🗺️"
                fieldName="mapzone"
                category="Zone"
              />
              <DataCard
                title="Reference Datum"
                value={wellInfo.reference_datum}
                icon="📏"
                fieldName="refdatum"
                category="Datum"
              />
              <DataCard
                title="Ground Level"
                value={wellInfo.ground_level_elevation}
                unit="m"
                icon="🏔️"
                fieldName="gle"
                category="Elevation"
              />
            </Section>
          </div>
        </div>

        {/* Right Column - Detailed Information */}
        <div className="space-y-4">
          {/* Geographic Coordinates */}
          <div className="bg-white dark:bg-gray-800 dark:text-white rounded-xl p-4 border border-gray-150 dark:border-gray-700">
            <Section 
              title="Geographic Coordinates" 
              description="Detailed coordinate measurements"
              columns={2}
            >
              <DataCard
                title="Latitude DMS"
                value={wellInfo.latitude_degrees !== null ? `${wellInfo.latitude_degrees}°${wellInfo.latitude_minutes}'${wellInfo.latitude_seconds}" N` : undefined}
                icon="🌎"
                fieldName="latdms"
                category="DMS Format"
              />
              <DataCard
                title="Longitude DMS"
                value={wellInfo.longitude_degrees !== null ? `${wellInfo.longitude_degrees}°${wellInfo.longitude_minutes}'${wellInfo.longitude_seconds}" E` : undefined}
                icon="🌎"
                fieldName="londms"
                category="DMS Format"
              />
            </Section>
          </div>
          

          {/* UTM System */}
          <div className="bg-white rounded-xl p-4 border border-gray-150">
            <Section title="UTM System" description="Universal Transverse Mercator coordinates">
              <DataCard
                title="Grid Correction"
                value={wellInfo.grid_correction}
                icon="📐"
                fieldName="gridcorrection"
                category="Correction"
              />
              <DataCard
                title="Central Meridian"
                value={wellInfo.central_meridian}
                icon="🧭"
                fieldName="meridian"
                category="Reference"
              />
            </Section>
          </div>

          {/* Technical Specifications */}
          <div className="bg-white rounded-xl p-4 border border-gray-150">
            <Section title="Technical Specifications" description="Well technical parameters" columns={3}>
              <DataCard
                title="W(t)"
                value={wellInfo.w_t}
                icon="⚖️"
                fieldName="wt"
                category="Weight"
              />
              <DataCard
                title="Min W(t)"
                value={wellInfo.min_wt}
                icon="📉"
                fieldName="minwt"
                category="Minimum"
              />
              <DataCard
                title="Max W(t)"
                value={wellInfo.max_wt}
                icon="📈"
                fieldName="maxwt"
                category="Maximum"
              />
              <DataCard
                title="G(t)"
                value={wellInfo.g_t}
                icon="⚖️"
                fieldName="gt"
                category="Gravity"
              />
              <DataCard
                title="Min G(t)"
                value={wellInfo.min_gt}
                icon="📉"
                fieldName="mingt"
                category="Minimum"
              />
              <DataCard
                title="Max G(t)"
                value={wellInfo.max_gt}
                icon="📈"
                fieldName="maxgt"
                category="Maximum"
              />
            </Section>
          </div>

          {/* Reference System */}
          
        </div>
      </div>

      {/* Copy Feedback */}
      {copiedField && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-3 py-1.5 rounded text-xs shadow-lg animate-fadeIn">
          ✓ Copied {copiedField}
        </div>
      )}
    </div>
  );
}